/* eslint-disable @typescript-eslint/no-unused-vars */


import "../../styles/style.css"

import React, { useEffect, useState } from "react";
import DataTable, { Column, Action } from "../../app/helper/tables/DataTable";
import {
  getPartner,
  getPartnerList,
  updatePartner,
  setPartnerStatus,
  resetPartnerPassword,
  IPartner,
  PartnerDetailResponse,
  deletePartnerApi,
  createPartner,
} from "../../services/partnerService";
import {
  FiEye as FiEyeRaw,
  FiEdit2 as FiEdit2Raw,
  FiKey as FiKeyRaw,
  FiTrash2 as FiDeleteRaw,
} from "react-icons/fi";

type IconFC = React.FC<{ size?: number; className?: string }>;
const FiEye = FiEyeRaw as IconFC;
const FiEdit2 = FiEdit2Raw as IconFC;
const FiKey = FiKeyRaw as IconFC;
const FiDelete = FiDeleteRaw as IconFC;

interface User {
  _id: number | string;
  name: string;
  email: string;
  role: string;
  active?: boolean | string;
  partner: { name: string; city: string };
  bookCount:number | string;
  partnerCount:number | string

}
type ModalMode = "create" | "edit" | "detail" | null;

const emptyForm = { name: "", email: "", contactName: "" };

const PartnersTable: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [partner, setPartners] = useState<[]>([]);
  const [totalPages, setTotalPages] = useState(0)
  const [modal, setModal] = useState<ModalMode>(null);
  const [selected, setSelected] = useState<IPartner | null>(null);
  const [detail, setDetail] = useState<PartnerDetailResponse | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const [partnerDelete, setPartnerDelete] = useState<IPartner | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<IPartner | null>(null);
  const [resetTarget, setResetTarget] = useState<IPartner | null>(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);

  const [feedback, setFeedback] = useState<{ type: "error" | "success"; text: string } | null>(null);

  
  const fetchPartners = async () => {
    try {
      const res = await getPartnerList(page, limit);
      if (res.success) {
        setPartners(res?.data || []);
        setTotalPages(res?.pagination?.totalPages || 0);
      }
    } catch (error) {
      console.log("show partners fetching error:", error);
    }
  };
  // console.log("show partners-->",partner);/
  

  useEffect(() => {
    fetchPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ---------- Create ----------
  const openCreate = () => {
    setForm(emptyForm);
    setSelected(null);
    setDetail(null);
    setModal("create");
    setFeedback(null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFeedback(null);
    try {
      const result = await createPartner(form);
      const msg = result.emailSent
        ? "Partner created. Login credentials sent by email."
        : `Partner created. Temporary password: ${result.temporaryPassword}`;
      setFeedback({ type: "success", text: msg });
      await fetchPartners();
      closeModal();
    } catch (err: unknown) {
      setFeedback({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to create partner",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- View ----------
  const openDetail = async (partner: IPartner) => {
    setSelected(partner);
    setModal("detail");
    setFeedback(null);
    try {
      const data = await getPartner(partner._id);
      setDetail(data);
    } catch (err: unknown) {
      setFeedback({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to load partner details",
      });
    }
  };

  // ---------- Edit ----------
  const openEdit = (partner: IPartner) => {
    setSelected(partner);
    setForm({
      name: partner.name,
      email: partner.email,
      contactName: partner.contactName || "",
    });
    setModal("edit");
    setFeedback(null);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      await updatePartner(selected._id, form);
      setFeedback({ type: "success", text: "Partner updated." });
      await fetchPartners();
      closeModal();
    } catch (err: unknown) {
      setFeedback({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to update partner",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setModal(null);
    setSelected(null);
    setDetail(null);
  };

  // ---------- Activate / Deactivate ----------
  const handleToggleStatus = async (partner: IPartner, active: boolean) => {
    try {
      await setPartnerStatus(partner._id, active);
      await fetchPartners();
      setFeedback({
        type: "success",
        text: active ? "Partner activated." : "Partner deactivated.",
      });
    } catch (err: unknown) {
      setFeedback({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to update status",
      });
    }
  };

  const handleDeactivateClick = (partner: IPartner) => {
    setDeactivateTarget(partner);
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    const target = deactivateTarget;
    setDeactivateTarget(null);
    await handleToggleStatus(target, false);
  };

  // ---------- Reset password ----------
  const handleResetPasswordClick = (partner: IPartner) => {
    setResetTarget(partner);
  };

  const confirmResetPassword = async () => {
    if (!resetTarget) return;
    const target = resetTarget;
    setResetSubmitting(true);
    setFeedback(null);
    try {
      const result = await resetPartnerPassword(target._id);
      const msg = result.emailSent
        ? `New password emailed to ${target.email}.`
        : `${result.message}. Temporary password: ${result.temporaryPassword}`;
      setFeedback({ type: "success", text: msg });
      setResetTarget(null);
    } catch (err: unknown) {
      setFeedback({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to reset password",
      });
    } finally {
      setResetSubmitting(false);
    }
  };

  // ---------- Delete ----------
  const openDelete = (partner: IPartner) => {
    setPartnerDelete(partner);
    setFeedback(null);
  };

  const deletePartnerHandler = async () => {
    if (!partnerDelete) return;
    try {
      const result = await deletePartnerApi(partnerDelete._id);
      if (result?.success) {
        await fetchPartners();
        setFeedback({ type: "success", text: "Partner deleted successfully." });
      }
    } catch (error) {
      setFeedback({
        type: "error",
        text: error instanceof Error ? error.message : "Failed to delete partner",
      });
    } finally {
      setPartnerDelete(null);
    }
  };

  // ---------- Table columns ----------
  const columns: Column<User>[] = [
    { key: "name", header: "Name", sortable: true },
    { key: "email", header: "Email", sortable: true },
    {
      key: "active",
      header: "Status",
      sortable: true,
      render: (row) => {
        const isActive = row?.active === true;
        return (
          <button
            type="button"
            role="switch"
            aria-checked={isActive}
            title={isActive ? "Deactivate" : "Activate"}
            onClick={() => {
              const p = row as unknown as IPartner;
              if (isActive) {
                handleDeactivateClick(p);
              } else {
                handleToggleStatus(p, true);
              }
            }}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              isActive ? "bg-green-500" : "bg-slate-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isActive ? "translate-x-4" : "translate-x-1"
              }`}
            />
          </button>
        );
      },
    },
  ];

  // ---------- Table actions ----------
  const actions: Action<User>[] = [
    {
      label: "View",
      icon: <FiEye size={15} />,
      onClick: (row) => openDetail(row as unknown as IPartner),
    },
    {
      label: "Edit",
      icon: <FiEdit2 size={15} />,
      onClick: (row) => openEdit(row as unknown as IPartner),
    },
    {
      label: "Reset Password",
      icon: <FiKey size={15} />,
      onClick: (row) => handleResetPasswordClick(row as unknown as IPartner),
    },
    {
      label: "Delete Partner",
      icon: <FiDelete size={15} />, 
      danger: true,
      disabled:(row)=> row?.bookCount !== 0 || row?.partnerCount !== 0,
      onClick: (row) => openDelete(row as unknown as IPartner),
    },
  ];

  return (
    <div className="max-w-4xl mx-auto mt-4 sm:mt-10 px-3 sm:px-5 pb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Team Members</h2>
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white"
          onClick={openCreate}
        >
          + Create Partner
        </button>
      </div>

      {feedback && !modal && !resetTarget && !deactivateTarget && !partnerDelete && (
        <div
          className={`mb-4 px-4 py-2 rounded-lg text-sm ${
            feedback.type === "error" ? "bg-red-50 text-red-600" : "bg-green-50 text-green-700"
          }`}
        >
          {feedback.text}
        </div>
      )}

      <DataTable columns={columns} data={partner} actions={actions} pageSize={limit} totalPages={totalPages} rowKey="_id" />

      {/* ---------- Reset password confirm ---------- */}
      {resetTarget && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => !resetSubmitting && setResetTarget(null)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-sm w-full shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-slate-800 mb-2 text-center">
              Reset partner password?
            </h3>
            <p className="text-sm text-slate-500 mb-5">
              A new secure password will be generated and emailed to{" "}
              <strong>{resetTarget.name}</strong> ({resetTarget.email}).
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200"
                onClick={() => setResetTarget(null)}
                disabled={resetSubmitting}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white disabled:opacity-50"
                onClick={confirmResetPassword}
                disabled={resetSubmitting}
              >
                {resetSubmitting ? "Resetting..." : "Reset & Email Password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Deactivate confirm ---------- */}
      {deactivateTarget && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setDeactivateTarget(null)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-sm w-full shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-slate-800 mb-2 text-center">Deactivate partner?</h3>
            <p className="text-sm text-slate-500 mb-5">
              Are you sure you want to deactivate <strong>{deactivateTarget.name}</strong> (
              {deactivateTarget.email})? They will not be able to log in until reactivated.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200"
                onClick={() => setDeactivateTarget(null)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white"
                onClick={confirmDeactivate}
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Delete confirm ---------- */}
      {partnerDelete && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setPartnerDelete(null)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-sm w-full shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-slate-800 mb-2 text-center">Delete partner?</h3>
            <p className="text-sm text-slate-500 mb-5">
              Are you sure you want to permanently delete <strong>{partnerDelete.name}</strong> (
              {partnerDelete.email})? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200"
                onClick={() => setPartnerDelete(null)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white"
                onClick={deletePartnerHandler}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Create / Edit / Detail modal ---------- */}
      {modal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-lg w-full shadow-lg max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {modal === "create" && (
              <form onSubmit={handleCreate}>
                <h3 className="font-semibold text-slate-800 mb-4">Create Partner</h3>
                {feedback && (
                  <div
                    className={`mb-3 px-3 py-2 rounded-lg text-sm ${
                      feedback.type === "error"
                        ? "bg-red-50 text-red-600"
                        : "bg-green-50 text-green-700"
                    }`}
                  >
                    {feedback.text}
                  </div>
                )}
                <div className="space-y-3">
                  <label className="block text-sm text-slate-600">
                    Partner name
                    <input
                      className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-1.5"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </label>
                  <label className="block text-sm text-slate-600">
                    Email
                    <input
                      type="email"
                      className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-1.5"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value.trim() })}
                      required
                    />
                  </label>
                  {/* <label className="block text-sm text-slate-600">
                    Contact name (optional)
                    <input
                      className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-1.5"
                      value={form.contactName}
                      onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                    />
                  </label> */}
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  A secure random password will be generated and emailed to the partner.
                </p>
                <div className="flex justify-end gap-2 mt-5">
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm rounded-lg border border-slate-200"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white disabled:opacity-50"
                    disabled={submitting}
                  >
                    {submitting ? "Creating..." : "Create & Send Credentials"}
                  </button>
                </div>
              </form>
            )}

            {modal === "edit" && (
              <form onSubmit={handleEditSubmit}>
                <h3 className="font-semibold text-slate-800 mb-4">Edit Partner</h3>
                {feedback && (
                  <div
                    className={`mb-3 px-3 py-2 rounded-lg text-sm ${
                      feedback.type === "error"
                        ? "bg-red-50 text-red-600"
                        : "bg-green-50 text-green-700"
                    }`}
                  >
                    {feedback.text}
                  </div>
                )}
                <div className="space-y-3">
                  <label className="block text-sm text-slate-600">
                    Partner name
                    <input
                      className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-1.5"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </label>
                  <label className="block text-sm text-slate-600">
                    Email
                    <input
                      type="email"
                      className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-1.5"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value.trim() })}
                      required
                    />
                  </label>
                  {/* <label className="block text-sm text-slate-600">
                    Contact name
                    <input
                      className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-1.5"
                      value={form.contactName}
                      onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                    />
                  </label> */}
                </div>
                <div className="flex justify-end gap-2 mt-5">
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm rounded-lg border border-slate-200"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white disabled:opacity-50"
                    disabled={submitting}
                  >
                    {submitting ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            )}

            {modal === "detail" && selected && (
              <>
                <h3 className="font-semibold text-slate-800">{selected.name}</h3>
                <p className="text-sm text-slate-500 mb-4">{selected.email}</p>
                {!detail ? (
                  <p className="text-sm text-slate-400">Loading details...</p>
                ) : (
                  <div className="space-y-4 text-sm text-slate-700">
                    <div>
                      <h4 className="font-medium mb-1">Overview</h4>
                      <ul className="space-y-0.5 text-slate-500">
                        <li>Status: {detail.partner.active ? "Active" : "Inactive"}</li>
                        <li>Projects: {detail.stats.projectCount}</li>
                        <li>Books: {detail.stats.bookCount}</li>
                        <li>Recent clips: {detail.stats.clipCount}</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">Projects</h4>
                      <ul className="space-y-0.5 text-slate-500">
                        {detail.projects.length === 0 ? (
                          <li>No projects</li>
                        ) : (
                          detail.projects.map((p) => <li key={p._id}>{p.projectName}</li>)
                        )}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">Recent TTS Activity</h4>
                      <ul className="space-y-0.5 text-slate-500">
                        {detail.recentActivity.length === 0 ? (
                          <li>No clips yet</li>
                        ) : (
                          detail.recentActivity.map((c) => (
                            <li key={c._id}>
                              {c.bookTitle} — {c.chapter} / {c.clipName}
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  </div>
                )}
                <div className="flex justify-end mt-5">
                  <button
                    className="px-3 py-1.5 text-sm rounded-lg border border-slate-200"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnersTable;